<template>
  <div
    class="accordion-item"
    :class="[
      $blockClass(blockClass, 'accordion-item'),
      {'is-active': visible},
      {'has-tick': hasTickIcon}
    ]"
  >
    <button
      class="accordion-item__button accordion-button"
      :class="[
        $blockClass(blockClass, 'accordion-button')
      ]"
      @click="handleAccordionClick(index)"
    >
      <icon-tick
        v-if="hasTickIcon"
      />
      <span
        class="accordion-item__button-text text-label-s"
        :class="[
          $blockClass(blockClass, 'accordion-button-text')
        ]"
      >
        <slot name="title" />
      </span>
      <icon-enclosed-plus
        v-if="!hasTickIcon"
      />
    </button>

    <transition
      :css="animate"
      name="accordion-item"
      @enter="updateStartAccordionHeight"
      @after-enter="updateEndAccordionHeight"
      @before-leave="updateStartAccordionHeight"
      @after-leave="updateEndAccordionHeight"
    >
      <div
        v-if="visible"
        class="accordion-item__content"
        :class="[
          $blockClass(blockClass, 'accordion-content')
        ]"
      >
        <slot name="content" />
      </div>
    </transition>
  </div>
</template>

<script>

/**
 * Vue: Accordion Item (accordion-item)
 * -----------------------------------------------------------------------------
 * Accordion item component.
 * -Uses vue transition and scoped styles to handle accordion animation.
 *
 */
import { nextTick } from 'vue'
import IconEnclosedPlus from '~icons/misc/enclosed-plus.svg'
import IconTick from '~icons/misc/tick.svg'

export default {
  name: 'AccordionItem',

  components: {
    IconEnclosedPlus,
    IconTick,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },

    blockClass: {
      type: String,
      default: '',
    },

    index: {
      type: [Number, Boolean],
      default: false,
    },

    group: {
      type: [Number, Boolean],
      default: false,
    },

    content: {
      type: [String, Boolean, Object],
      default: '',
    },

    toggleIndex: {
      type: Object,
      default: () => {},
    },

    multiOpen: {
      type: Boolean,
      default: false,
    },

    hasTickIcon: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      visible: false,
      animate: false,
    }
  },

  watch: {
    toggleIndex() {
      this.handleAccordionToggle()
    },
  },

  mounted() {
    this.initOpenState()
  },

  methods: {

    /**
     * Handles accordion item click
     */
    handleAccordionClick() {
      if (!this.content) {
        return
      }

      this.$emit('accordionToggle', this.index, this.group)
    },

    /**
     * Toggle accordion item active state.
     */
    handleAccordionToggle() {
      if (this.group !== false && this.toggleIndex.group !== this.group) {
        return
      }

      if (this.toggleIndex.index === this.index) {
        this.visible = !this.visible
      }

      if (!this.multiOpen && this.toggleIndex.index !== this.index) {
        this.visible = false
      }
    },

    /**
     * Initializes accordion open state and animation transition
     */
    initOpenState() {
      if (!this.active) {
        this.animate = true
        return
      }

      this.visible = true

      nextTick(() => {
        this.animate = true
      })
    },

    /**
     * Update accordion open item height.
     * @param {Object} el - accordion dom element
     */
    updateStartAccordionHeight(el) {
      el.style.height = el.scrollHeight + 'px'
    },

    /**
     * Update accordion close item height.
     * @param {Object} el - accordion dom element
     */
    updateEndAccordionHeight(el) {
      el.style.height = ''
    },
  },
}
</script>

<style lang="scss">
@import './accordion-item';
</style>

